import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { RiskStatus, Severity } from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { SvgIconType } from '../svg-icon'

/** A finding's colour: its severity, or `review` where it put a risk into "confirm this". */
export type Tone = Severity | 'review'

/**
 * Every status colour in Inspector Gadget. Red, amber, emerald and sky mean something specific here and
 * have no theme token, so they live in this one map and nowhere else. Written out as complete literals:
 * Tailwind only builds classes it can read whole, and Ace markers are class names in Ace's own DOM.
 */
export const STATUS_STYLE = {
  risk: {
    flagged: { icon: XCircle, iconColor: 'text-red-600', text: 'text-red-800', rail: 'border-l-red-500' },
    review: { icon: AlertTriangle, iconColor: 'text-amber-600', text: 'text-amber-800', rail: 'border-l-amber-400' },
    clear: { icon: CheckCircle2, iconColor: 'text-emerald-600', text: 'text-foreground', rail: 'border-l-emerald-500' },
  } satisfies Record<RiskStatus, { icon: SvgIconType; iconColor: string; text: string; rail: string }>,

  tone: {
    high: {
      label: 'High',
      pill: 'border-red-200 bg-red-100 text-red-800',
      bar: 'border-l-red-400',
      marker: 'absolute z-[1] bg-red-400/30',
    },
    medium: {
      label: 'Medium',
      pill: 'border-amber-200 bg-amber-100 text-amber-800',
      bar: 'border-l-amber-400',
      marker: 'absolute z-[1] bg-amber-400/30',
    },
    review: {
      label: 'Confirm',
      pill: 'border-amber-200 bg-amber-100 text-amber-800',
      bar: 'border-l-amber-400',
      marker: 'absolute z-[1] bg-amber-400/30',
    },
    low: {
      label: 'Low',
      pill: 'border-sky-200 bg-sky-100 text-sky-800',
      bar: 'border-l-sky-400',
      marker: 'absolute z-[1] bg-sky-400/25',
    },
    info: {
      label: 'Info',
      pill: 'border-border bg-muted text-muted-foreground',
      bar: 'border-l-border',
      marker: 'absolute z-[1] bg-muted-foreground/20',
    },
  } satisfies Record<Tone, { label: string; pill: string; bar: string; marker: string }>,

  /* Calls that reach stored data, and targets outside the platform: worth a second look, not a violation. */
  attention: {
    chip: 'border-amber-300 bg-amber-100 text-amber-900',
    row: 'border-l-2 border-l-amber-400 bg-amber-50',
    rowCode: 'font-semibold text-amber-900',
    rowText: 'text-amber-800',
    text: 'text-amber-700',
  },

  counts: {
    passed: 'text-emerald-700',
    toConfirm: 'font-medium text-amber-700',
    violations: 'font-medium text-red-700',
  },

  notice: 'bg-amber-50 text-amber-800',

  /* The row jumped to, over any tint, in the same primary as the buttons that jump there. `absolute`: Ace positions markers behind the text. */
  activeMarker: 'absolute z-[2] border-y border-primary/90 bg-primary/40',
} as const

export const TONE_RANK: Tone[] = ['high', 'medium', 'review', 'low', 'info']
